/**
 * Online-Shop (Mitarbeiter): Typen der API /api/webshop (server/src/modules/webshop/routes.ts, docs/ARCHITECTURE.md §9.10),
 * Abfragen und Bezeichnungen.
 */
import { useQuery } from '@tanstack/react-query';
import { api, ApiError, qs } from '../../lib/api';
import { fmtDateTime, fmtTime } from '../../lib/format';

export type OrderStatus = 'new' | 'ready' | 'picked_up' | 'cancelled' | 'expired';
export type Availability = 'in_stock' | 'few' | 'soldout';
export type SupplyStatus = 'normal' | 'incoming' | 'discontinued';
export type ListingMode = 'auto' | 'show' | 'hide';

export interface StaffOrderItem {
  id: number; productId: number; name: string; qty: number; unitPriceCents: number; depositCents: number; vatRate: number;
  /** Preis/Pfand heute (null = Artikel gibt es nicht mehr) */
  currentPriceCents: number | null; currentDepositCents: number | null; priceChanged: boolean; productActive: boolean; ageRestricted: boolean;
  /** Preis je Stück an der Kasse: Reservierungspreis, ist der Kassenpreis heute niedriger, dieser (fehlt bei älteren Servern) */
  payPriceCents?: number;
}

export interface StaffCheckout { id: number; cartUuid: string; createdAt: number; createdBy: number | null; createdByName: string | null }

export interface StaffOrder {
  id: number; code: string; status: OrderStatus; customerName: string; customerPhone: string | null; customerEmail: string | null; note: string | null;
  totalCents: number; depositCents: number; currentTotalCents: number; priceChanged: boolean; ageRestricted: boolean;
  createdAt: number; readyAt: number | null; expiresAt: number; closedAt: number | null; closedBy: number | null; closedByName: string | null;
  cancelReason: string | null; cancelledByCustomer: boolean; checkoutCartUuid: string | null; saleId: number | null; receiptNo: number | null;
  anonymized: boolean; items: StaffOrderItem[];
  noShowCount: number; blocked: boolean; blockId: number | null;
  verified: boolean; verifiedAt: number | null; verifiedBy: number | null; verifiedByName: string | null;
  pinFailures: number; pinLocked: boolean; handoverNote: string | null;
  /** Der verknüpfte Bon enthält nur einen Teil der Reservierung (Artikel fehlt oder weniger Menge) */
  saleMismatch: boolean;
  /** Der verknüpfte Bon wurde storniert (Status bleibt „Abgeholt“) */
  saleVoided: boolean;
  saleVoidedAt?: number | null;
  /** Liegt an einer Kasse (offene, nicht gelöste Übernahme; noch abholbar) – seit checkoutAt (neueste Übernahme) */
  atRegister: boolean;
  checkoutAt: number | null;
  /** Alle offenen Übernahmen, neueste zuerst (mehr als eine = liegt an mehreren Kassen) */
  openCheckouts?: StaffCheckout[];
  /** Weitere offene Bestellungen vom selben Absender (gleicher Internetanschluss), ohne diese */
  sameSenderOpen: number;
}

export interface FindResult { orders: StaffOrder[]; scanned: boolean; verifiedId: number | null }
export interface OrderCounts { new: number; ready: number }

export interface Block {
  id: number; label: string; reason: string | null; auto: boolean; noShowCount: number; active: boolean;
  createdAt: number; createdBy: number | null; createdByName: string | null; liftedAt: number | null; liftedBy: number | null; liftedByName: string | null;
  /** automatisch aufgehoben (eine Nicht-Abholung wurde doch noch abgeholt) */
  liftedAuto?: boolean;
}

export interface ListingCategory { id: number; name: string; color: string | null; active: boolean; visible: boolean; ageRestricted: boolean; productCount: number; visibleCount: number }
export interface ListingProduct {
  id: number; name: string; categoryId: number | null; categoryName: string | null; priceCents: number; depositCents: number; unit: string;
  openPrice: boolean; trackStock: boolean; stock: number; reserved: number; mode: ListingMode; visible: boolean; hiddenReason: string | null;
  description: string | null; imageUrl: string | null; imageFileId: number | null; maxQty: number | null; effectiveMaxQty: number;
  /** Artikelbild aus Artikel & Lager (ab 2.9.0) – im Shop gezeigt, solange kein eigenes Shop-Bild hochgeladen ist */
  productImageUrl?: string | null;
  supplyStatus: SupplyStatus; supplyNote: string | null; availability: Availability; availableQty: number | null; ageRestricted: boolean;
}
export interface Listings { categories: ListingCategory[]; products: ListingProduct[] }

export interface WebshopSettings {
  enabled: boolean; holdHours: number; maxOrderCents: number; maxQty: number; maxOpenOrders: number; countLimit: number;
  openingHours: string; pickupInfo: string; notice: string; notifyEmail: string; emailCustomers: boolean; anonymizeDays: number;
  noshowLimit: number; websiteUrl: string; shopUrl: string;
}
export interface SettingsResponse { settings: WebshopSettings; effective: { shopUrl: string | null; websiteUrl: string | null }; smtpConfigured: boolean }

// ---------------------------------------------------------------------------
// Bezeichnungen
// ---------------------------------------------------------------------------
export const STATUS_LABEL: Record<OrderStatus, string> = {
  new: 'Neu', ready: 'Liegt bereit', picked_up: 'Abgeholt', cancelled: 'Storniert', expired: 'Nicht abgeholt',
};
export const STATUS_TONE: Record<OrderStatus, 'good' | 'warn' | 'crit' | 'info' | undefined> = {
  new: 'crit', ready: 'info', picked_up: 'good', cancelled: undefined, expired: 'warn',
};
export const isOpen = (o: Pick<StaffOrder, 'status'>) => o.status === 'new' || o.status === 'ready';
/** Übergabe möglich (PIN prüfen, An die Kasse, Abgeholt): offen – oder abgelaufen, wenn der Kunde trotzdem kommt (§9.10). */
export const canHandover = (o: Pick<StaffOrder, 'status'>) => isOpen(o) || o.status === 'expired';

/** „seit 14:05“ (heute) bzw. „seit 14.10.2026, 18:30“. */
export function sinceText(ts: number | null | undefined, now = Date.now()): string {
  if (ts == null) return '';
  return new Date(ts).toDateString() === new Date(now).toDateString() ? fmtTime(ts) : fmtDateTime(ts);
}

/**
 * Sieht die Eingabe wie ein gescannter Abhol-Code mit PIN aus („R4821-7305“, „R-4821-7305“; Scanner mit deutscher
 * Tastatur liefern „ß“ statt „-“)? Dann POST /orders/scan (finden und bestätigen), sonst nur suchen. Gleiche Regel wie der Server.
 */
export const PICKUP_CODE_RE = /^r[^0-9a-z]?(\d{4,8})[^0-9a-z]{1,2}(\d{4})$/i;
export const looksLikePickupCode = (q: string) => PICKUP_CODE_RE.test(q.trim());

/** tel:-Link nur aus Ziffern (und führendem +), sonst null. */
export function telHref(phone: string | null | undefined): string | null {
  const t = (phone ?? '').trim();
  const digits = t.replace(/\D/g, '');
  return digits.length >= 3 ? `tel:${t.startsWith('+') ? '+' : ''}${digits}` : null;
}
/** mailto:-Link – Adresse vollständig kodiert (keine eingeschleusten ?cc=/&body=). */
export const mailHref = (email: string) => `mailto:${encodeURIComponent(email.trim())}`;

/** Reservierung ↔ Bon: je Artikel reserviert und im Bon (Positionen vergleichen bei „Bon weicht ab“). */
export interface SaleCompareRow { productId: number; name: string; reserved: number; sold: number; status: 'ok' | 'less' | 'missing' | 'extra' }
export function compareSale(
  order: Array<{ productId: number; name: string; qty: number }>,
  sale: Array<{ product_id: number | null; kind: string; name: string; qty: number }>,
): SaleCompareRow[] {
  const sold = new Map<number, { name: string; qty: number }>();
  for (const s of sale) {
    if (s.kind !== 'product' || s.product_id == null) continue;
    const cur = sold.get(s.product_id);
    sold.set(s.product_id, { name: cur?.name ?? s.name, qty: (cur?.qty ?? 0) + s.qty });
  }
  const reserved = new Map<number, { name: string; qty: number }>();
  for (const i of order) {
    const cur = reserved.get(i.productId);
    reserved.set(i.productId, { name: cur?.name ?? i.name, qty: (cur?.qty ?? 0) + i.qty });
  }
  const rows: SaleCompareRow[] = [];
  for (const [productId, r] of reserved) {
    const q = sold.get(productId)?.qty ?? 0;
    rows.push({ productId, name: r.name, reserved: r.qty, sold: q, status: q <= 0 ? 'missing' : q < r.qty ? 'less' : 'ok' });
  }
  for (const [productId, s] of sold) if (!reserved.has(productId) && s.qty > 0) rows.push({ productId, name: s.name, reserved: 0, sold: s.qty, status: 'extra' });
  return rows;
}

export const MODE_LABEL: Record<ListingMode, string> = { auto: 'Automatisch (nach Warengruppe)', show: 'Immer zeigen', hide: 'Nie zeigen' };
export const MODE_SHORT: Record<ListingMode, string> = { auto: 'Automatisch', show: 'Immer zeigen', hide: 'Nie zeigen' };
export const SUPPLY_LABEL: Record<SupplyStatus, string> = { normal: 'Normal', incoming: 'Nachschub unterwegs', discontinued: 'Nicht mehr lieferbar' };

/** Online-Anzeige wie im Shop (§9.10): „Auf Lager“ / „Noch N Stück“ / „Ausverkauft“. */
export function availabilityText(a: Availability, qty: number | null): string {
  if (a === 'soldout') return 'Ausverkauft';
  if (a === 'few') return `Noch ${qty ?? 0} Stück`;
  return 'Auf Lager';
}
export const availabilityTone = (a: Availability): 'good' | 'warn' | 'crit' => (a === 'soldout' ? 'crit' : a === 'few' ? 'warn' : 'good');

/** Verständliche Meldung für die wichtigen Fehler der Abholung (Du-Form). */
export function staffErrorMessage(e: unknown): string {
  if (e instanceof ApiError) {
    switch (e.code) {
      case 'WRONG_PIN': {
        const rem = (e.details as { remaining?: number } | undefined)?.remaining;
        return rem != null ? `Die PIN stimmt nicht. Noch ${rem} ${rem === 1 ? 'Versuch' : 'Versuche'} – danach geht es nur noch mit Grund.` : e.message;
      }
      case 'PIN_LOCKED': return 'Zu viele falsche PINs. Übergib die Bestellung nur nach Prüfung (z. B. Ausweis) mit „Ohne PIN übergeben“ und Grund.';
      case 'NOT_VERIFIED': return 'Bitte erst die Abhol-PIN prüfen (oder den Abhol-Code scannen) – oder „Ohne PIN übergeben“ mit Grund wählen.';
      case 'INVALID_STATUS': return `${e.message} Die Liste wird aktualisiert.`;
      default: return e.message;
    }
  }
  return e instanceof Error ? e.message : 'Unbekannter Fehler';
}

/** Verbleibende Zeit bis zur Abholfrist, kurz („noch 5 Std.“, „abgelaufen“). */
export function timeLeft(expiresAt: number, now = Date.now()): { text: string; urgent: boolean } {
  const ms = expiresAt - now;
  if (ms <= 0) return { text: 'Frist abgelaufen', urgent: true };
  const min = Math.round(ms / 60_000);
  if (min < 60) return { text: `noch ${min} Min.`, urgent: true };
  const h = Math.round(min / 60);
  if (h < 48) return { text: `noch ${h} Std.`, urgent: h < 3 };
  return { text: `noch ${Math.round(h / 24)} Tage`, urgent: false };
}

// ---------------------------------------------------------------------------
// Abfragen
// ---------------------------------------------------------------------------
export const KEYS = {
  all: ['webshop'] as const,
  orders: (filter: 'open' | 'done', q: string) => ['webshop', 'orders', filter, q] as const,
  order: (id: number) => ['webshop', 'order', id] as const,
  count: ['webshop', 'count'] as const,
  blocks: ['webshop', 'blocks'] as const,
  listings: ['webshop', 'listings'] as const,
  settings: ['webshop', 'settings'] as const,
};

export function useOrders(filter: 'open' | 'done', q = '') {
  return useQuery({
    queryKey: KEYS.orders(filter, q),
    queryFn: () => api.get<{ orders: StaffOrder[] }>(`/webshop/orders${qs({ filter, q: q || undefined, limit: filter === 'done' ? 100 : 300 })}`),
    refetchInterval: filter === 'open' ? 30_000 : false,
  });
}

export function useOrder(id: number | null, initial?: StaffOrder) {
  return useQuery({
    queryKey: KEYS.order(id ?? 0),
    queryFn: () => api.get<StaffOrder>(`/webshop/orders/${id}`),
    enabled: id != null,
    initialData: initial && initial.id === id ? initial : undefined,
    initialDataUpdatedAt: 0,
    refetchInterval: 20_000,
  });
}

export function useBlocks(enabled = true) {
  return useQuery({ queryKey: KEYS.blocks, queryFn: () => api.get<{ blocks: Block[] }>('/webshop/blocks'), enabled });
}

export function useListings() {
  return useQuery({ queryKey: KEYS.listings, queryFn: () => api.get<Listings>('/webshop/listings') });
}

export function useWebshopSettings() {
  return useQuery({ queryKey: KEYS.settings, queryFn: () => api.get<SettingsResponse>('/webshop/settings') });
}

// ---------------------------------------------------------------------------
// Ton bei neuer Bestellung (je Gerät, Standard aus)
// ---------------------------------------------------------------------------
const SOUND_KEY = 'kiosk.webshop.sound';
export const soundEnabled = (): boolean => { try { return localStorage.getItem(SOUND_KEY) === '1'; } catch { return false; } };
export const setSoundEnabled = (on: boolean) => { try { if (on) localStorage.setItem(SOUND_KEY, '1'); else localStorage.removeItem(SOUND_KEY); } catch { /* ignorieren */ } };

/** Dezenter Zwei-Ton-Hinweis (ohne Tondatei). Der Browser spielt Töne erst nach einer ersten Bedienung der Seite. */
export function chime() {
  try {
    const Ctx = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const t0 = ctx.currentTime;
    [660, 880].forEach((f, i) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = 'sine';
      o.frequency.value = f;
      g.gain.setValueAtTime(0.0001, t0 + i * 0.18);
      g.gain.exponentialRampToValueAtTime(0.07, t0 + i * 0.18 + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, t0 + i * 0.18 + 0.3);
      o.connect(g).connect(ctx.destination);
      o.start(t0 + i * 0.18);
      o.stop(t0 + i * 0.18 + 0.32);
    });
    setTimeout(() => void ctx.close(), 900);
  } catch { /* ohne Ton */ }
}
